import { useEffect, useRef, useState } from "react";
import { CardQuestion, CardActions, PressEnterHint } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import type { CardDef } from "../types";

interface TextCardProps {
  card: CardDef;
  value: string;
  onValue: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  index: number;
  error?: string;
  inputType?: "text" | "email" | "tel";
  loading?: boolean;
}

export function TextCard({
  card,
  value,
  onValue,
  onNext,
  onBack,
  onSkip,
  index,
  error,
  inputType = "text",
  loading = false,
}: TextCardProps) {
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, [card.id]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTouched(true);
      onNext();
    }
  };

  const showError = touched && error;

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: () => {
        setTouched(true);
        onNext();
      },
      onCancel: onBack,
      continueLoading: loading,
      onSkip,
    }),
    [onNext, onBack, loading, onSkip],
  );

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      <div className="relative max-w-md mt-4">
        <input
          ref={inputRef}
          type={inputType}
          value={value}
          onChange={(e) => {
            onValue(e.target.value);
            if (touched) setTouched(false);
          }}
          onKeyDown={handleKey}
          placeholder={card.placeholder}
          disabled={loading}
          autoComplete={
            inputType === "email"
              ? "email"
              : inputType === "tel"
                ? "tel"
                : "given-name"
          }
          className={`w-full bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 py-3.5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)] disabled:opacity-60 disabled:cursor-not-allowed ${
            showError 
              ? "!border-red-400 focus:!border-red-500" 
              : "hover:border-gray-400"
          }`}
        />
        {showError && (
          <div className="mt-2 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm font-medium text-red-500">{error}</p>
          </div>
        )}
      </div>

      <div className="mt-8">
        <CardActions
          onNext={() => { setTouched(true); onNext(); }}
          onBack={onBack}
          onSkip={onSkip}
          nextLoading={loading}
        />
        {!loading && <PressEnterHint />}
      </div>
    </div>
  );
}
